import {
  Grid,
  Slider,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  LinearProgress,
  Button,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../../App'

const Quotes = () => {
  const [loading, setLoading] = useState(true)
  const [formValues, setFormValues] = useState({})
  const [fetchError, setFetchError] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const { setOrgConfig, orgConfig } = useContext(TenantSettingsContext)
  const { orgSettings } = { ...orgConfig }

  const profitMargin = formValues?.defaultProfitMargin ?? 0

  useEffect(() => {
    API.get(`/settings?select=quoteInvoices`)
      .then((res) => {
        if (res.status === 200) {
          setFormValues(res.data.quoteInvoices)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const handleSubmitChanges = () => {
    API.put('/site-config', { quoteInvoices: formValues }).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Settings Successfully Updated' })
        setOrgConfig((prev) => ({
          ...prev,
          orgConfig: {
            ...prev.orgConfig,
            ...res.data,
          },
        }))
      }
    })
  }

  return (
    <Stack>
      {loading ? (
        <LinearProgress />
      ) : (
        <>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="title2">
                Quotes & Invoice Settings
              </Typography>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Typography variant="title3" fontSize="0.9rem" marginTop={1}>
                Default Profit Margin
              </Typography>
              <Stack direction="row" padding={1} gap={2}>
                <Slider
                  value={profitMargin}
                  color={
                    profitMargin >= 20
                      ? 'success'
                      : profitMargin >= 10
                        ? 'warning'
                        : 'error'
                  }
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      defaultProfitMargin: parseInt(e.target.value),
                    }))
                  }
                />
                <TextField
                  variant="standard"
                  autoComplete="off"
                  value={profitMargin}
                  onChange={(e) =>
                    e.target.value === ''
                      ? setFormValues((prev) => ({
                          ...prev,
                          defaultProfitMargin: 0,
                        }))
                      : e.target.value >= 0 &&
                        e.target.value <= 100 &&
                        setFormValues((prev) => ({
                          ...prev,
                          defaultProfitMargin: parseInt(e.target.value),
                        }))
                  }
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  type="number"
                  sx={{ width: '100px' }}
                />
              </Stack>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="title3">Business Costs</Typography>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                value={formValues?.defaultAdminCharge ?? 0}
                onChange={(e) =>
                  e.target.value >= 0 &&
                  setFormValues((prev) => ({
                    ...prev,
                    defaultAdminCharge: parseFloat(e.target.value),
                  }))
                }
                label="Default Admin Charge"
                fullWidth
                type="number"
                autoComplete="off"
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: (
                    <InputAdornment position="start">
                      {orgSettings?.currency ?? '£'}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                value={formValues?.makeReadyCost ?? 0}
                onChange={(e) =>
                  e.target.value === ''
                    ? setFormValues((prev) => ({
                        ...prev,
                        makeReadyCost: 0,
                      }))
                    : e.target.value >= 0 &&
                      e.target.value <= 100 &&
                      setFormValues((prev) => ({
                        ...prev,
                        makeReadyCost: parseInt(e.target.value),
                      }))
                }
                fullWidth
                label="Make Ready Cost"
                helperText="Business cost per vehicle at an event"
                type="number"
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: (
                    <InputAdornment position="start">
                      {orgSettings?.currency ?? '£'}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                value={formValues?.costPerMile ?? 0}
                type="number"
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '' || isNaN(e.target.value)) {
                    setFormValues((prev) => ({
                      ...prev,
                      costPerMile: 0,
                    }))
                  } else {
                    const numValue = parseFloat(value)
                    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                      setFormValues((prev) => ({
                        ...prev,
                        costPerMile: numValue,
                      }))
                    }
                  }
                }}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                label="Cost per mile"
                helperText="This will multiply the number of miles per vehicle by this amount."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {orgSettings?.currency ?? '£'}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                value={formValues?.defaultTreatmentCentreBuildCost ?? 0}
                onChange={(e) =>
                  e.target.value === ''
                    ? setFormValues((prev) => ({
                        ...prev,
                        defaultTreatmentCentreBuildCost: 0,
                      }))
                    : e.target.value >= 0 &&
                      e.target.value <= 100 &&
                      setFormValues((prev) => ({
                        ...prev,
                        defaultTreatmentCentreBuildCost: parseInt(
                          e.target.value,
                        ),
                      }))
                }
                fullWidth
                type="number"
                label="Default Treatment Centre Build"
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: (
                    <InputAdornment position="start">
                      {orgSettings?.currency ?? '£'}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                value={formValues?.schedulingCharge ?? 0}
                onChange={(e) =>
                  e.target.value === ''
                    ? setFormValues((prev) => ({
                        ...prev,
                        schedulingCharge: 0,
                      }))
                    : e.target.value >= 0 &&
                      e.target.value <= 100 &&
                      setFormValues((prev) => ({
                        ...prev,
                        schedulingCharge: parseInt(e.target.value),
                      }))
                }
                fullWidth
                label="Scheduling Charge"
                type="number"
                InputProps={{
                  inputProps: { min: 0 },
                  startAdornment: (
                    <InputAdornment position="start">
                      {orgSettings?.currency ?? '£'}
                    </InputAdornment>
                  ),
                }}
                helperText="Business cost per shift"
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                value={formValues?.defaultVehicleDailyCost ?? 0}
                type="number"
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '' || isNaN(value)) {
                    setFormValues((prev) => ({
                      ...prev,
                      defaultVehicleDailyCost: 0,
                    }))
                  } else {
                    const numValue = parseFloat(value)
                    if (!isNaN(numValue) && numValue >= 0) {
                      setFormValues((prev) => ({
                        ...prev,
                        defaultVehicleDailyCost: numValue,
                      }))
                    }
                  }
                }}
                inputProps={{ min: 0, step: 0.01 }}
                fullWidth
                label="Default Vehicle Daily Cost"
                helperText="Fallback used in event cost estimates when a vehicle doesn't have enough service history yet"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {orgSettings?.currency ?? '£'}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                size="small"
                color="success"
                variant="contained"
                onClick={handleSubmitChanges}
              >
                Submit Changes
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  )
}

export default Quotes
